import React, { createContext, useReducer, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useQuoteState } from 'src/stateProviders/QuoteStateProvider';
import { getOppositeEdge } from 'src/pages/quotations/views/blocks/utils/edgeUtils';
import EdgeType from 'src/enums/edgeType';
import EdgeLabel from 'src/enums/edgeLabel';
import { Block, Edge, Segment } from 'src/types/shared/blocksV3';
import getParentBlock from 'src/utils/getParentBlockV3';
import getBlockLabel from 'src/utils/getBlockLabel';
import BlockType from 'src/enums/blockType';
import getBlockChildren from 'src/utils/getBlockChildrenV3';
import CANVAS_SCALE_FACTOR from 'src/constants/scale';
import {
	DEPTH_COEFFICIENT,
	STEPS_COEFFICIENT,
} from 'src/constants/coefficients';
import { getBlockInitialPosition } from 'src/pages/quotations/views/blocks/utils/getBlockPosition';
import { getBlockUpdatedPosition } from 'src/pages/quotations/views/blocks/utils/getUpdatedBlockPosition';
import { getUpdatedBlockEdges } from 'src/pages/quotations/views/blocks/utils/getUpdatedBlockEdges';
import getBlock from 'src/utils/getBlockV3';

import {
	getDefaultLandingForSegment,
	constructLanding,
} from 'src/pages/quotations/views/blocks/utils/getDefaultLandingV3';
import {
	getDefaultStairForSegment,
	constructStair,
} from 'src/pages/quotations/views/blocks/utils/getDefaultStairV3';
import {
	MIN_STAIR_VIABLE_BLOCK_HEIGHT,
	MAX_STAIR_HEIGHT,
} from 'src/constants/limits';
import { getDefaultDeckboxForEdgeAndSegment } from 'src/pages/quotations/views/blocks/utils/getDefaultDeckbox';
import getDefaultBumpout from 'src/pages/quotations/views/blocks/utils/getDefaultBumpout';
import { getBlocksWithCascadedUpdates } from 'src/pages/quotations/views/blocks/utils/updateChildBlocks';
import { toastError } from 'src/Toaster/Toast';

export interface DeckState {
	projectName: string;
	blocks: Block[];
	selectedBlock: Block | null;
	selectedEdge: { blockId: string | null; edgeLabel: EdgeLabel | null };
	displayedBlockModal: Block | null;
	scaleFactor: number;
	canvasBounds: {
		width: number;
		height: number;
		offsetX: number;
		offsetY: number;
	} | null;
	zoomScale: number;
	dropbeamNeeded: {
		show: boolean;
		block: Block;
		dropbeamsNeeded: number;
	} | null;
	dropbeamNotNeeded: {
		show: boolean;
		block: Block;
	} | null;
}

// Define the action types for the reducer
export type DeckAction =
	| { type: 'ADD_BLOCK'; payload: Block }
	| { type: 'SELECT_BLOCK'; payload: Block | null }
	| {
			type: 'SELECT_EDGE';
			payload: {
				blockId: string | null | undefined;
				edgeLabel: EdgeLabel | null | undefined;
			};
	  }
	| { type: 'UPDATE_BLOCK'; payload: Block }
	| {
			type: 'UPDATE_BLOCK_REF';
			payload: { id: string; ref: React.RefObject<any> };
	  }
	| { type: 'CLEAR_SELECTION' }
	| { type: 'DELETE_BLOCK'; payload: Block }
	| {
			type: 'DELETE_SEGMENT';
			payload: { block: Block; edgeId: string; segmentId: string };
	  }
	| { type: 'ADD_BUMPOUT'; payload: Block }
	| { type: 'ADD_STAIR'; payload: Block }
	| { type: 'ADD_LANDING'; payload: Block }
	| { type: 'INITIALIZE_STATE' }
	| { type: 'OPEN_BLOCK_MODAL'; payload: Block }
	| { type: 'CLOSE_BLOCK_MODAL' }
	| { type: 'UPDATE_BLOCKS'; payload: Block[] }
	| { type: 'SET_SCALE_FACTOR'; payload: number }
	| {
			type: 'SET_CANVAS_BOUNDS';
			payload: {
				width: number;
				height: number;
				offsetX: number;
				offsetY: number;
			};
	  }
	| { type: 'SET_ZOOM_SCALE'; payload: number }
	| { type: 'CHANGE_EDGE_TYPE'; payload: { segment: Segment; type: EdgeType } }
	| {
			type: 'SET_DROPBEAM_NEEDED';
			payload: { show: boolean; block: Block; dropbeamsNeeded: number };
	  }
	| {
			type: 'SET_DROPBEAM_NOT_NEEDED';
			payload: { show: boolean; block: Block };
	  };

export type DeckContextType = {
	state: DeckState;
	dispatch: React.Dispatch<DeckAction>;
	pricingContainerRef: React.RefObject<HTMLDivElement> | null;
	transformWrapperRef: React.RefObject<any> | null;
};

// Initial State
const initialState: DeckState = {
	projectName: 'John test 10',
	blocks: [], // Array of all blocks (DeckBoxes, Bumpouts, Stairs)
	selectedBlock: null, // Currently selected block
	selectedEdge: { blockId: null, edgeLabel: null }, // Currently selected edge { blockId, edgeLabel }
	displayedBlockModal: null,
	scaleFactor: CANVAS_SCALE_FACTOR / 12,
	canvasBounds: null,
	zoomScale: 1,
	dropbeamNeeded: null,
	dropbeamNotNeeded: null,
};

// Create Context
export const DeckContext = createContext<DeckContextType>({
	state: initialState,
	dispatch: () => null,
	pricingContainerRef: null,
	transformWrapperRef: null,
});

// Edge Types
export const DeckBoxEdgeTypes = [
	EdgeType.NO_RAILING,
	EdgeType.RAILING,
	EdgeType.STAIR,
	EdgeType.LANDING,
	EdgeType.DECKBOX,
	EdgeType.BUMPOUT,
	EdgeType.HOUSE,
	EdgeType.HOUSE_ATTACHED,
	EdgeType.HOUSE_FREESTANDING,
];

export const LandingEdgeTypes = [
	EdgeType.RAILING,
	EdgeType.NO_RAILING,
	EdgeType.BLOCK_ATTACHED,
	EdgeType.LANDING_ATTACHED,
	EdgeType.STAIR_ATTACHED,
	EdgeType.HOUSE_ATTACHED,
	EdgeType.HOUSE,
	EdgeType.GROUND,
	EdgeType.STAIR,
];

export const BumpoutEdgeTypes = [
	EdgeType.NO_RAILING,
	EdgeType.DECKING,
	EdgeType.HOUSE,
];

export const StairEdgeTypes = [
	EdgeType.NO_RAILING,
	EdgeType.RAILING,
	EdgeType.BLOCK_ATTACHED,
	EdgeType.LANDING_ATTACHED,
	EdgeType.STAIR_ATTACHED,
	EdgeType.GROUND,
	EdgeType.LANDING,
];

// Function to get edge color based on type
export const getEdgeColor = (type: EdgeType, isSelected: boolean) => {
	switch (type) {
		case 'House':
		case 'Bump out':
			return '#800000'; // Deep maroon
		case 'House Attached':
			return '#774E9F'; // Indigo
		case 'Railing':
			return isSelected ? '#3874CB' : '#5D9DFC'; // Bright blue
		case 'Decking':
		case 'Deckbox':
			return '#228B22'; // Forest green
		case 'No Railing':
			return 'rgba(0, 0, 0, 0)'; // Dim gray
		case 'Ground':
			return '#9A5650'; // Saddle brown
		case 'Block Attached':
			return '#FF6600'; // Orange
		case 'Landing Attached':
		case 'Landing':
			return '#FF8833';
		case 'Stair Attached':
		case 'Stair':
			return '#FFB066';
		case 'House Freestanding':
			return '#808080';
		default:
			return 'rgba(200, 200, 200, 0.5)'; // Light gray
	}
};

// Function to get edge border color based on type
export const getEdgeBorderColor = (type: EdgeType) => {
	switch (type) {
		case 'No Railing':
			return '#97C15C'; // green
		default:
			return null;
	}
};

// Helper function to recursively reposition nested children when parent is resized
export const repositionNestedChildrenRecursively = (
	parentBlock: Block,
	scaleFactor: number,
	allBlocks?: Block[],
	blockRegistry?: Map<string, Block>
): Block => {
	const registry = blockRegistry || new Map<string, Block>();

	// 1. Logic for intermediate landing re-addition has been moved to updateChildBlocks.ts
	// repositionNestedChildrenRecursively is now strictly for updating positions.
	let baseBlock = parentBlock;

	// Reposition bumpouts - add to registry AFTER recursive processing
	const updatedBumpouts = baseBlock.bumpouts?.map((bumpout) => {
		const repositionedBumpout = {
			...bumpout,
			position: getBlockUpdatedPosition(baseBlock, bumpout, scaleFactor),
		};
		const finalBumpout = repositionNestedChildrenRecursively(
			repositionedBumpout,
			scaleFactor,
			allBlocks,
			registry
		);
		// Preserve the original ref
		if (finalBumpout.id) {
			registry.set(finalBumpout.id, finalBumpout);
		}
		return {
			...finalBumpout,
			ref: bumpout.ref,
		};
	});

	// Unified processing for Stairs and Landings to handle dependencies (Stair->Landing->Stair)
	// We use a multi-pass approach:
	// Pass 1: Direct children of baseBlock
	// Pass 2+: Children dependant on updated blocks

	let pendingStairs = [...(baseBlock.stairs || [])];
	let pendingLandings = [...(baseBlock.landings || [])];

	const processedStairs: Block[] = [];
	const processedLandings: Block[] = [];

	// Helper to process a single block
	// Return true if processed, false if parent dependencies not met
	const tryProcessBlock = (
		block: Block,
		type: 'STAIR' | 'LANDING'
	): boolean => {
		// Determine parent
		const isDirectChild = block.parentBlock?.id === baseBlock.id;
		let parentBlockForPosition = baseBlock;
		let parentFound = true;

		if (!isDirectChild && block.parentBlock?.id) {
			const registeredParent = registry.get(block.parentBlock.id);
			// If parent is not baseBlock and not in registry, check if it's currently being processed (recursion check? no, sequential check)
			// If parent is in pending lists, we can't process this child yet.
			if (registeredParent) {
				parentBlockForPosition = registeredParent;
			} else {
				// If parent is supposed to be in this update cycle but hasn't been processed yet, wait.
				// Assuming all relevant parents are either baseBlock or in the pending lists.
				const parentIsStair = pendingStairs.some(
					(s) => s.id === block.parentBlock.id
				);
				const parentIsLanding = pendingLandings.some(
					(l) => l.id === block.parentBlock.id
				);

				if (parentIsStair || parentIsLanding) {
					return false; // Defer processing
				}
				// If not found in pending, maybe it's missing or from outer scope.
				// We proceed with baseBlock/fallback to avoid infinite loop,
				// OR we assume it was processed in previous recursion step?
				// Current logic falls back to baseBlock or looks in 'allBlocks'.
				// Let's stick to existing fallback logic but prioritize correct order.
				parentFound = false;
			}
		}

		// If parent not found in registry and not pending, we might have a data issue or it's a root sibling.
		// Fallback or use baseBlock (existing logic did this implicitly by failing to find in registry)
		if (!isDirectChild && !parentFound && block.parentBlock?.id) {
			const parentInStairs = baseBlock.stairs?.find(
				(s) => s.id === block.parentBlock.id
			);
			if (parentInStairs) {
				// It IS in this block's arrays but we haven't processed it effectively?
				// It should have been caught by "parentIsStair" check above if we logic correctly.
			}
		}

		const repositionedBlock = {
			...block,
			position: getBlockUpdatedPosition(
				parentBlockForPosition,
				block,
				scaleFactor
			),
		};

		const finalBlock = repositionNestedChildrenRecursively(
			repositionedBlock,
			scaleFactor,
			allBlocks,
			registry
		);

		if (finalBlock.id) {
			registry.set(finalBlock.id, finalBlock);
		}

		if (type === 'STAIR')
			processedStairs.push({ ...finalBlock, ref: block.ref });
		else processedLandings.push({ ...finalBlock, ref: block.ref });

		return true;
	};

	// Loop until no progress made or all done
	let progress = true;
	while (progress && (pendingStairs.length > 0 || pendingLandings.length > 0)) {
		progress = false;

		// Try Stairs
		const remainingStairs = [];
		for (const s of pendingStairs) {
			if (tryProcessBlock(s, 'STAIR')) {
				progress = true;
			} else {
				remainingStairs.push(s);
			}
		}
		pendingStairs = remainingStairs;

		// Try Landings
		const remainingLandings = [];
		for (const l of pendingLandings) {
			if (tryProcessBlock(l, 'LANDING')) {
				progress = true;
			} else {
				remainingLandings.push(l);
			}
		}
		pendingLandings = remainingLandings;
	}

	// Process any remaining (circular deps or orphans) to ensure nothing is lost
	if (pendingStairs.length > 0)
		pendingStairs.forEach((s) => tryProcessBlock(s, 'STAIR'));
	if (pendingLandings.length > 0)
		pendingLandings.forEach((l) => tryProcessBlock(l, 'LANDING'));

	// Sort back to original order? Or purely by processed?
	// Arrays in React state usually stable, but here we rebuilt them.
	// Let's assume order implies z-index or stability is nice.
	// But since we split them, we might lose relative order between stairs?
	// Stairs are typically independent.
	const updatedStairs = processedStairs;
	const updatedLandings = processedLandings;

	// Update parent edges if any child stair depth changed (for side-attached stairs)

	return {
		...baseBlock,
		...baseBlock,
		...(updatedBumpouts !== undefined && { bumpouts: updatedBumpouts }),
		...(updatedLandings !== undefined && { landings: updatedLandings }),
		...(updatedStairs !== undefined && { stairs: updatedStairs }),
	};
};

// Helper function to recursively reposition flat children (blocks stored at root level)
// when parent is resized
const repositionFlatChildrenRecursively = (
	allBlocks: Block[],
	parentId: string,
	updatedParent: Block,
	scaleFactor: number,
	blockRegistry: Map<string, Block>,
	processedIds: Set<string>
): Block[] => {
	// Prevent infinite loops by tracking processed blocks
	if (processedIds.has(parentId)) {
		return allBlocks;
	}
	processedIds.add(parentId);

	// Find all flat children of this parent (blocks at root level with parentBlock.id === parentId)
	const flatChildren = allBlocks.filter(
		(blk) =>
			blk.parentBlock?.id === parentId &&
			(blk.type === BlockType.DECKBOX ||
				blk.type === BlockType.LANDING ||
				blk.type === BlockType.STAIR)
	);

	if (flatChildren.length === 0) {
		return allBlocks;
	}

	// Reposition each flat child and recursively handle their descendants
	let updatedBlocks = [...allBlocks];

	flatChildren.forEach((child) => {
		// Get the latest version of the parent from registry
		const currentParent = blockRegistry.get(parentId) || updatedParent;

		// Calculate new position for this child based on updated parent
		const repositionedChild = {
			...child,
			position: getBlockUpdatedPosition(currentParent, child, scaleFactor),
		};

		// Recursively reposition this child's nested children (bumpouts, nested stairs/landings)
		let fullyRepositionedChild = repositionedChild;
		if (
			repositionedChild.bumpouts?.length > 0 ||
			repositionedChild.stairs?.length > 0 ||
			repositionedChild.landings?.length > 0
		) {
			fullyRepositionedChild = repositionNestedChildrenRecursively(
				repositionedChild,
				scaleFactor,
				updatedBlocks,
				blockRegistry
			);
		}

		// Add to registry for use by descendants
		blockRegistry.set(fullyRepositionedChild.id, fullyRepositionedChild);

		// Update the block in the blocks array
		updatedBlocks = updatedBlocks.map((blk) =>
			blk.id === child.id
				? {
						...fullyRepositionedChild,
						ref: blk.ref,
					}
				: blk
		);

		// Recursively reposition this child's flat children (grandchildren, great-grandchildren, etc.)
		updatedBlocks = repositionFlatChildrenRecursively(
			updatedBlocks,
			child.id,
			fullyRepositionedChild,
			scaleFactor,
			blockRegistry,
			processedIds
		);
	});

	return updatedBlocks;
};

// Helper function to pre-populate the registry with flat children (DeckBoxes, Landings at root level)
// This ensures that when nested stairs are processed, their flat parent blocks are already in the registry
const collectFlatChildrenForRegistry = (
	allBlocks: Block[],
	parentId: string,
	updatedParent: Block,
	scaleFactor: number,
	blockRegistry: Map<string, Block>,
	processedIds: Set<string>
): void => {
	// Prevent infinite loops
	if (processedIds.has(parentId)) {
		return;
	}
	processedIds.add(parentId);

	// Find all flat children of this parent (blocks at root level with parentBlock.id === parentId)
	const flatChildren = allBlocks.filter(
		(blk) =>
			blk.parentBlock?.id === parentId &&
			(blk.type === BlockType.DECKBOX || blk.type === BlockType.LANDING)
	);

	if (flatChildren.length === 0) {
		return;
	}

	// For each flat child, calculate its new position and add to registry
	flatChildren.forEach((child) => {
		// Get the latest version of the parent from registry
		const currentParent = blockRegistry.get(parentId) || updatedParent;

		// Calculate new position for this child based on updated parent
		const repositionedChild = {
			...child,
			position: getBlockUpdatedPosition(currentParent, child, scaleFactor),
		};

		// Add to registry (but don't modify blocks array yet - that happens in repositionFlatChildrenRecursively)
		blockRegistry.set(repositionedChild.id, repositionedChild);

		// Recursively collect this child's flat children
		collectFlatChildrenForRegistry(
			allBlocks,
			child.id,
			repositionedChild,
			scaleFactor,
			blockRegistry,
			processedIds
		);
	});
};

// Reducer
const deckReducer = (state: DeckState, action) => {
	switch (action.type) {
		case 'SET_SCALE_FACTOR': {
			return {
				...state,
				scaleFactor: action.payload,
			};
		}

		case 'SET_CANVAS_BOUNDS': {
			return {
				...state,
				canvasBounds: action.payload,
			};
		}

		case 'SET_ZOOM_SCALE': {
			return {
				...state,
				zoomScale: action.payload,
			};
		}

		case 'ADD_BLOCK':
			return addBlock(state, action.payload);

		case 'SELECT_BLOCK': {
			return {
				...state,
				selectedBlock: action.payload,
				selectedEdge: null, // Reset selected edge when a new block is selected
			};
		}

		case 'SELECT_EDGE':
			return selectEdge(
				state,
				action.payload.blockId,
				action.payload.edgeLabel
			);

		case 'OPEN_BLOCK_MODAL': {
			return {
				...state,
				displayedBlockModal: action.payload,
			};
		}

		case 'CLOSE_BLOCK_MODAL':
			return {
				...state,
				displayedBlockModal: null,
			};

		case 'UPDATE_BLOCK_REF': {
			// Recursive helper to update refs at any nesting level
			const updateBlockRef = (blocks: Block[]): Block[] => {
				return blocks.map((block) => {
					if (block.id === action.payload.id) {
						return { ...block, ref: action.payload.ref };
					}
					// Recursively update nested blocks
					return {
						...block,
						...(block.bumpouts?.length && {
							bumpouts: updateBlockRef(block.bumpouts),
						}),
						...(block.stairs?.length && {
							stairs: updateBlockRef(block.stairs),
						}),
						...(block.landings?.length && {
							landings: updateBlockRef(block.landings),
						}),
					};
				});
			};

			const updatedBlocks = updateBlockRef(state.blocks);
			return {
				...state,
				blocks: updatedBlocks,
				...(state.selectedBlock?.id === action.payload.id && {
					selectedBlock: { ...state.selectedBlock, ref: action.payload.ref },
				}),
			};
		}

		case 'UPDATE_BLOCK': {
			return updateBlock(state, action.payload);
		}

		case 'UPDATE_BLOCKS': {
			return {
				...state,
				blocks: action.payload,
			};
		}
		case 'CLEAR_SELECTION':
			return {
				...state,
				selectedBlock: null,
				selectedEdge: null,
			};

		case 'DELETE_BLOCK': {
			return deleteBlock(state, action.payload);
		}
		case 'DELETE_SEGMENT': {
			const attachedBlocks = [
				...(action.payload.block.bumpouts || []),
				...(action.payload.block.stairs || []),
				...(action.payload.block.landings || []),
			].filter((blk) => blk.parentBlock.segmentId == action.payload.segmentId);

			const allChildren = attachedBlocks.reduce((acc, blk) => {
				const children = getBlockChildren(state.blocks, blk);

				return [...acc, ...children];
			}, []);

			const blocksToDeleteIds = [...attachedBlocks, ...allChildren].map(
				(blk) => blk.id
			);

			return {
				...state,
				blocks: state.blocks.map((blk) => {
					if (blk.id === action.payload.block.id) {
						const updatedBlock = {
							...blk,
							bumpouts: blk.bumpouts.filter(
								(b) => !blocksToDeleteIds.includes(b.id)
							),
							landings: blk.landings?.filter(
								(l) => !blocksToDeleteIds.includes(l.id)
							),
							stairs: blk.stairs?.filter(
								(s) => !blocksToDeleteIds.includes(s.id)
							),
						};

						updatedBlock.edges = updatedBlock?.edges.map((ed) => {
							if (ed.id == action.payload.edgeId) {
								const updatedEdge = { ...ed };

								const segmentToDelete = updatedEdge.segments.find(
									(seg) => seg.id == action.payload.segmentId
								);

								updatedEdge.segments = updatedEdge.segments.filter(
									(seg) => seg.id != action.payload.segmentId
								);

								const segmentsLength = updatedEdge.segments.reduce(
									(acc, segment) => acc + segment.length,
									0
								);

								if (segmentsLength < updatedEdge.length) {
									const deckboxes = state.blocks.filter(
										(blk) => blk.type == BlockType.DECKBOX
									);
									const landings = deckboxes.flatMap((dk) => dk.landings);
									const stairs = deckboxes.flatMap((dk) => dk.stairs);
									const bumpouts = deckboxes.flatMap((dk) => dk.bumpouts);

									const allBlocks = [
										...deckboxes,
										...landings,
										...stairs,
										...bumpouts,
									];

									const vacantSegmentIndex = updatedEdge.segments.findIndex(
										(Segment) =>
											allBlocks.every(
												(blk) => blk?.parentBlock.segmentId != Segment.id
											)
									);

									if (vacantSegmentIndex > -1) {
										const vacantSegment =
											updatedEdge.segments[vacantSegmentIndex];

										updatedEdge.segments.splice(vacantSegmentIndex, 1, {
											...vacantSegment,
											length:
												vacantSegment.length +
												Math.min(
													segmentToDelete.length,
													updatedEdge.length - segmentsLength
												),
										});
									}
								}

								return updatedEdge;
							} else {
								return ed;
							}
						});

						return updatedBlock;
					}

					return blk;
				}),
				selectedBlock: null,
				selectedEdge: null,
				displayedBlockModal: null,
			};
		}
		case 'ADD_BUMPOUT': {
			const bumpout = action.payload;
			return addBumpout(state, bumpout);
		}
		// Action to add a Stair to a DeckBox
		case 'ADD_STAIR': {
			const stair = action.payload;
			return addStair(state, stair);
		}
		case 'ADD_LANDING': {
			const landing = action.payload;

			return addLanding(state, landing);
		}
		case 'CHANGE_EDGE_TYPE': {
			const { segment, type: newType } = action.payload;

			return changeEdgeType({ state, segment, newType });
		}
		case 'SET_DROPBEAM_NEEDED': {
			const { show, block, dropbeamsNeeded } = action.payload;

			return {
				...state,
				dropbeamNeeded: {
					show,
					block,
					dropbeamsNeeded,
				},
			};
		}
		case 'SET_DROPBEAM_NOT_NEEDED': {
			const { show, block } = action.payload;

			return {
				...state,
				dropbeamNotNeeded: {
					show,
					block,
				},
			};
		}

		// Migration: Ensure all DeckBoxes have a position
		case 'INITIALIZE_STATE': {
			return {
				...state,
				blocks: state.blocks.map((blk) =>
					blk.type === 'DeckBox'
						? {
								...blk,
								position: blk.position || { x: 100, y: 100 },
							}
						: blk
				),
			};
		}

		default:
			console.warn(`Unhandled action type: ${action.type}`);
			return state;
	}
};

// Provider Component
export const DeckProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const qouteState = useQuoteState();

	initialState.blocks = qouteState.blocks.length > 0 ? qouteState.blocks : [];

	const [state, dispatch] = useReducer(deckReducer, initialState);

	const pricingContainerRef = React.useRef<HTMLDivElement>(null);
	const transformWrapperRef = React.useRef<any>(null);

	// Dispatch INITIALIZE_STATE once when the provider mounts
	React.useEffect(() => {
		dispatch({ type: 'INITIALIZE_STATE' });
	}, []);

	return (
		<DeckContext.Provider
			value={{ state, dispatch, pricingContainerRef, transformWrapperRef }}
		>
			{children}
		</DeckContext.Provider>
	);
};

function deleteBlock(state: DeckState, block: Block) {
	const allChildren = getBlockChildren(state.blocks, block);

	const blocksToDeleteIds = [block, ...allChildren].map((blk) => blk.id);

	return {
		...state,
		blocks: state.blocks
			.filter((blk) => !blocksToDeleteIds.includes(blk.id))
			.map((blk) => {
				const edges = blk.edges.map((edge) => {
					edge.segments = edge.segments.map((segment) => {
						if (segment.id == block.parentBlock.segmentId) {
							return { ...segment, type: EdgeType.RAILING };
						}
						return segment;
					});
					return edge;
				});

				if (blk.type === 'DeckBox') {
					return {
						...blk,
						edges,
						bumpouts: blk.bumpouts?.filter(
							(b) => !blocksToDeleteIds.includes(b.id)
						),
						landings: blk.landings
							?.filter((l) => !blocksToDeleteIds.includes(l.id))
							?.map((landing) => {
								const lEdges = landing.edges.map((edge) => {
									edge.segments = edge.segments.map((segment) => {
										if (segment.id == block.parentBlock.segmentId) {
											return { ...segment, type: EdgeType.RAILING };
										}
										return segment;
									});
									return edge;
								});

								return { ...landing, edges: lEdges };
							}),
						stairs: blk.stairs
							?.filter((s) => !blocksToDeleteIds.includes(s.id))
							?.map((stair) => {
								const sEdges = stair.edges.map((edge) => {
									edge.segments = edge.segments.map((segment) => {
										if (segment.id == block.parentBlock.segmentId) {
											return { ...segment, type: EdgeType.RAILING };
										}
										return segment;
									});
									return edge;
								});

								return { ...stair, edges: sEdges };
							}),
					};
				}

				return blk;
			}),
		selectedBlock: null,
		selectedEdge: null,
		displayedBlockModal: null,
	};
}

function addLanding(state: DeckState, landing: Block) {
	const { parentDeckBoxId, parentBlock } = getParentBlock(
		state.blocks,
		landing.parentBlock
	);

	if (!parentBlock) {
		console.warn(`Block with id ${landing.parentBlock.id} not found.`);
		return state;
	}

	// Check if the landing is attached to a block
	const blockEdge: Edge = landing.edges.find((edge) => edge.attachedToParent);

	const newLanding = {
		...landing,
		id: landing.id || uuidv4(),
		position: getBlockInitialPosition(parentBlock, landing, state.scaleFactor),
		rotation: 0, // Initialize rotation
	};

	return {
		...state,
		blocks: state.blocks.map((blk) => {
			if (blk.type === 'DeckBox') {
				const newBlock = { ...blk };

				if (blk.id === parentDeckBoxId) {
					newBlock.landings = [
						...blk.landings,
						{
							...newLanding,
							label: getBlockLabel(state.blocks, newLanding),
						},
					];
				}

				if (parentBlock.type == BlockType.STAIR) {
					const edgeAttachedTo = getOppositeEdge(blockEdge.label);

					if (blk.stairs)
						newBlock.stairs = blk.stairs.map((stair) =>
							stair.id == parentBlock.id
								? {
										...stair,
										edges: stair.edges.map((edge) => {
											if (
												edge.label !== edgeAttachedTo ||
												edge.attachedToParent
											) {
												return edge;
											}
											return {
												...edge,
												type: EdgeType.LANDING_ATTACHED,
											};
										}),
									}
								: stair
						);
				}

				return newBlock;
			}

			return blk;
		}),
		selectedBlock: newLanding,
	};
}

function addStair(state: DeckState, stair: Block) {
	const { parentDeckBoxId, parentBlock } = getParentBlock(
		state.blocks,
		stair.parentBlock
	);

	if (!parentBlock) {
		console.warn(`Block with id ${stair.parentBlock.id} not found.`);
		return state;
	}

	// Check if the stair is attached to the DeckBox
	const blockEdge = stair.edges.find((edge) => edge.attachedToParent);

	const newStair = {
		...stair,
		id: stair.id || uuidv4(),
		position: getBlockInitialPosition(parentBlock, stair, state.scaleFactor),
		rotation: 0, // Initialize rotation
	};

	return {
		...state,
		blocks: state.blocks.map((blk) => {
			if (blk.type === 'DeckBox') {
				const newBlock = { ...blk };

				if (blk.id === parentDeckBoxId) {
					newBlock.stairs = [
						...blk.stairs,
						{
							...newStair,
							label: getBlockLabel(state.blocks, newStair),
						},
					];
				}

				if (parentBlock.type == BlockType.LANDING) {
					const edgeAttachedTo = getOppositeEdge(blockEdge.label);

					if (blk.landings)
						newBlock.landings = blk.landings.map((landing) =>
							landing.id == parentBlock.id
								? {
										...landing,
										edges: landing.edges.map((edge) => {
											if (
												edge.label !== edgeAttachedTo ||
												edge.attachedToParent
											) {
												return edge;
											}
											return {
												...edge,
												segments: edge.segments.map((seg) => ({
													...seg,
													type: EdgeType.STAIR_ATTACHED,
												})),
											};
										}),
									}
								: landing
						);
				}

				return newBlock;
			}

			return blk;
		}),
		selectedBlock: newStair,
	};
}

function addBumpout(state: DeckState, bumpout: Block) {
	const { parentDeckBoxId, parentBlock } = getParentBlock(
		state.blocks,
		bumpout.parentBlock
	);

	if (!parentBlock) {
		console.warn(`Block with id ${bumpout.parentBlock.id} not found.`);
		return state;
	}

	const initialPosition = getBlockInitialPosition(
		parentBlock,
		bumpout,
		state.scaleFactor
	);

	const newBumpout = {
		...bumpout,
		id: bumpout.id || uuidv4(),
		position: initialPosition,
		rotation: 0, // Initialize rotation
	};

	return {
		...state,
		blocks: state.blocks.map((blk) => {
			if (blk.id === parentDeckBoxId && blk.type === 'DeckBox') {
				return {
					...blk,
					bumpouts: [
						...blk.bumpouts,
						{
							...newBumpout,
							label: getBlockLabel(state.blocks, newBumpout),
						},
					],
				};
			}
			return blk;
		}),
		selectedBlock: newBumpout,
	};
}

function addBlock(state: DeckState, block: Block) {
	if (block.type === 'DeckBox') {
		const { parentBlock } = getParentBlock(state.blocks, block.parentBlock);

		const label = getBlockLabel(state.blocks, block);
		const labelAlphabet = label.split(' ')[1];
		const deckBoxNumber = labelAlphabet.toUpperCase().charCodeAt(0) - 64;

		const positionOffset = 20 * deckBoxNumber;

		const defaultPosition = parentBlock
			? getBlockInitialPosition(parentBlock, block, state.scaleFactor)
			: block.position || {
					x: 100 + positionOffset,
					y: 100 + positionOffset,
				};

		const blockData = {
			...block,
			id: block.id || uuidv4(),
			position: defaultPosition, // Ensure position is set
			rotation: 0,
			bumpouts: [], // Initialize bumpouts array
			stairs: [], // Initialize stairs array
			landings: [],
			label,
		};

		return {
			...state,
			blocks: [...state.blocks, blockData],
			selectedBlock: blockData,
		};
	} else {
		// Handle other block types if necessary
		const blockData = {
			...block,
			id: block.id || uuidv4(),
			position: block.position || { x: 0, y: 0 },
		};

		return {
			...state,
			blocks: [...state.blocks, blockData],
			selectedBlock: blockData,
		};
	}
}

export function updateBlock(state: DeckState, block: Block) {
	let oldBlock = state.blocks.find((blk) => blk.id === block.id) as
		| Block
		| undefined;

	if (!oldBlock) {
		for (const blk of state.blocks) {
			const b = blk as Block;
			const found =
				(b.stairs || []).find((s) => s.id === block.id) ||
				(b.landings || []).find((l) => l.id === block.id);
			if (found) {
				oldBlock = found;
				break;
			}
		}
	}

	const dimensionsChanged =
		oldBlock &&
		(oldBlock.dimensions?.width !== block.dimensions?.width ||
			oldBlock.dimensions?.depth !== block.dimensions?.depth ||
			oldBlock.dimensions?.height !== block.dimensions?.height);

	const positionChanged =
		oldBlock &&
		(oldBlock.position?.x !== block.position?.x ||
			oldBlock.position?.y !== block.position?.y);

	const segmentsChanged = oldBlock?.edges?.some((oldEdge) => {
		const newEdge = block.edges?.find((e) => e.id === oldEdge.id);
		if (!newEdge) return false;
		return oldEdge.segments.some((oldSeg) => {
			const newSeg = newEdge.segments.find((s) => s.id === oldSeg.id);
			return newSeg && oldSeg.length !== newSeg.length;
		});
	});

	const updatedBlocks = state.blocks.map((blk) => {
		if (blk.id === block.id) {
			// Update DeckBox directly if IDs match
			const updatedBlock = {
				...blk,
				...block,
				edges: getUpdatedBlockEdges(state.blocks, block),
			};

			if (blk.ref && !updatedBlock.ref) {
				updatedBlock.ref = blk.ref;
			}

			// Reposition children if dimensions OR position changed and block has nested children
			if (
				updatedBlock.type === BlockType.DECKBOX &&
				(dimensionsChanged || positionChanged || segmentsChanged) &&
				(updatedBlock.bumpouts?.length > 0 ||
					updatedBlock.stairs?.length > 0 ||
					updatedBlock.landings?.length > 0)
			) {
				// Create a registry to track updated blocks for nested references
				const blockRegistry = new Map<string, Block>();
				blockRegistry.set(updatedBlock.id, updatedBlock);

				// Pre-populate registry with all flat children (DeckBoxes, Landings at root level)
				// This ensures that when nested stairs are processed, their flat parent blocks are already available
				const registryProcessedIds = new Set<string>();
				collectFlatChildrenForRegistry(
					state.blocks,
					updatedBlock.id,
					updatedBlock,
					state.scaleFactor,
					blockRegistry,
					registryProcessedIds
				);

				// Use recursive function to update all nested children (including grandchildren)
				// Pass the pre-populated registry so stairs can find their flat DeckBox parents
				const repositionedBlock = repositionNestedChildrenRecursively(
					updatedBlock,
					state.scaleFactor,
					state.blocks,
					blockRegistry
				);
				// Preserve the block's ref after repositioning
				return {
					...repositionedBlock,
					ref: blk.ref,
				};
			}

			return updatedBlock;
		} else if (blk.type === 'DeckBox') {
			// Check and update within Bumpouts
			const updatedBumpouts = blk.bumpouts?.map((bump) => {
				if (bump.id !== block.id) return bump;

				const { parentBlock } = getParentBlock(state.blocks, bump.parentBlock);

				if (!parentBlock) {
					console.warn(`Block with id ${bump.parentBlock.id} not found.`);
					return bump;
				}

				const updatedBumpout = {
					...bump,
					...block,
					position: getBlockUpdatedPosition(
						parentBlock,
						block,
						state.scaleFactor
					),
					edges: getUpdatedBlockEdges(state.blocks, block),
				};

				if (bump.ref && !updatedBumpout.ref) {
					updatedBumpout.ref = bump.ref;
				}

				return updatedBumpout;
			});

			// Check and update within Stairs
			const updatedStairs = blk.stairs?.map((stair) => {
				if (stair.id !== block.id) return stair;

				const { parentBlock } = getParentBlock(state.blocks, stair.parentBlock);

				if (!parentBlock) {
					console.warn(`Block with id ${stair.parentBlock.id} not found.`);
					return stair;
				}

				const _attachedEdge = block.edges?.find((e) => e.attachedToParent);
				const _isTopBottom =
					_attachedEdge?.label === EdgeLabel.TOP ||
					_attachedEdge?.label === EdgeLabel.BOTTOM;
				const updatedDisplayDimensions = {
					width: _isTopBottom ? block.dimensions.width : block.dimensions.depth,
					height: _isTopBottom
						? block.dimensions.depth
						: block.dimensions.width,
				};

				const updatedStair = {
					...stair,
					...block,
					displayDimensions: updatedDisplayDimensions,
					position: getBlockUpdatedPosition(
						parentBlock,
						block,
						state.scaleFactor
					),
					edges: getUpdatedBlockEdges(state.blocks, block),
				};

				if (stair.ref && !updatedStair.ref) {
					updatedStair.ref = stair.ref;
				}

				return updatedStair;
			});

			// Check and update within Landings
			const updatedLandings = blk.landings?.map((landing) => {
				if (landing.id !== block.id) return landing;

				const { parentBlock } = getParentBlock(
					state.blocks,
					landing.parentBlock
				);

				if (!parentBlock) {
					console.warn(`Block with id ${landing.parentBlock.id} not found.`);
					return landing;
				}

				const updatedLanding = {
					...landing,
					...block,
					position: getBlockUpdatedPosition(
						parentBlock,
						block,
						state.scaleFactor
					),
					edges: getUpdatedBlockEdges(state.blocks, block),
				};

				if (landing.ref && !updatedLanding.ref) {
					updatedLanding.ref = landing.ref;
				}

				return updatedLanding;
			});

			const { parentBlock } = getParentBlock(state.blocks, blk.parentBlock);

			return {
				...blk,
				bumpouts: updatedBumpouts,
				stairs: updatedStairs,
				landings: updatedLandings,
				position: getBlockUpdatedPosition(parentBlock, blk, state.scaleFactor),
				edges: getUpdatedBlockEdges(state.blocks, blk),
			};
		} else {
			// No update needed for other block types
			return blk;
		}
	});

	// If dimensions OR position changed, also update any top-level children in blocks array
	// This handles DeckBox, Landing, and Stair blocks stored at top level
	// Position changes occur during drag, dimension changes occur during resize
	let finalBlocks = updatedBlocks;
	if (dimensionsChanged || positionChanged || segmentsChanged) {
		// Apply height cascade logic (add/remove intermediate landings, update child dims)
		// Ensure we pass the updated version of the block (from updatedBlocks) rather than the raw payload if needed,
		// but getBlocksWithCascadedUpdates uses IDs to match children.
		// We pass 'block' (the payload with new dims) as the 'updatedBlock' reference.
		if (oldBlock) {
			// CRITICAL FIX: Merge payload with oldBlock to ensure children arrays (stairs/landings) are present
			const fullUpdatedBlock = { ...oldBlock, ...block };
			finalBlocks = getBlocksWithCascadedUpdates(
				finalBlocks,
				oldBlock,
				fullUpdatedBlock
			);
		}

		const updatedParent = finalBlocks.find((blk) => blk.id === block.id);

		if (!updatedParent && block.type === BlockType.STAIR) {
			const parentDeckbox = finalBlocks.find(
				(blk) =>
					blk.type === BlockType.DECKBOX &&
					((blk as Block).stairs || []).some((s) => s.id === block.id)
			) as Block | undefined;

			if (parentDeckbox) {
				const deckboxRegistry = new Map<string, Block>();
				deckboxRegistry.set(parentDeckbox.id, parentDeckbox);
				const repositionedDeckbox = repositionNestedChildrenRecursively(
					parentDeckbox,
					state.scaleFactor,
					finalBlocks,
					deckboxRegistry
				);
				finalBlocks = finalBlocks.map((b) =>
					b.id === repositionedDeckbox.id ? repositionedDeckbox : b
				);
			}
		}

		if (updatedParent && updatedParent.type === BlockType.DECKBOX) {
			// Create a registry to track updated blocks for nested references
			const blockRegistry = new Map<string, Block>();
			blockRegistry.set(updatedParent.id, updatedParent);

			// Create a set to track processed blocks to prevent infinite loops
			const processedIds = new Set<string>();

			// Pre-populate registry with all flat children BEFORE processing them
			// This ensures that when processing a flat child's nested children (e.g., Stair C in DeckBox B),
			// they can find their parent (DeckBox B) in the registry
			const registryProcessedIds = new Set<string>();
			collectFlatChildrenForRegistry(
				finalBlocks,
				updatedParent.id,
				updatedParent,
				state.scaleFactor,
				blockRegistry,
				registryProcessedIds
			);

			// Recursively reposition all flat children (and their flat children, etc.)
			// Note: Bumpouts are always nested in parent.bumpouts[], never top-level
			// Now the registry has all flat children, so their nested children can find them
			finalBlocks = repositionFlatChildrenRecursively(
				finalBlocks,
				updatedParent.id,
				updatedParent,
				state.scaleFactor,
				blockRegistry,
				processedIds
			);

			// CRITICAL FIX: Re-run nested children repositioning for the main updated block.
			// Why? 'getBlocksWithCascadedUpdates' (lines 1227) may have added NEW nested children (Landings/Stairs).
			// These new children have default (0,0) positions.
			// The previous repositioning (inside updatedBlocks .map loop) happened BEFORE they existed.
			// We need to calculate their positions now that they exist.
			const parentAfterCascade = finalBlocks.find(
				(b) => b.id === updatedParent.id
			);
			if (parentAfterCascade) {
				const repositionedParent = repositionNestedChildrenRecursively(
					parentAfterCascade,
					state.scaleFactor,
					finalBlocks, // PASS finalBlocks so it can find parents
					blockRegistry
				);

				// Update finalBlocks with the repositioned parent
				finalBlocks = finalBlocks.map((b) =>
					b.id === repositionedParent.id ? repositionedParent : b
				);
			}
		}
	}

	return {
		...state,
		blocks: finalBlocks,
		selectedBlock:
			state.selectedBlock && state.selectedBlock.id === block.id
				? { ...state.selectedBlock, ...block }
				: state.selectedBlock,
	};
}

function selectEdge(
	state: DeckState,
	blockId: string | null,
	edgeLabel: EdgeLabel | null
) {
	return {
		...state,
		selectedEdge: {
			blockId: blockId,
			edgeLabel: edgeLabel,
		},
		displayedBlockModal: null,
	};
}

export function changeEdgeType({
	state,
	segment,
	newType,
}: {
	state: DeckState;
	segment: Segment;
	newType: EdgeType;
}) {
	let block = getBlock(state.blocks, segment.blockId);
	if (!block) return;

	if (
		(newType == EdgeType.STAIR_ATTACHED ||
			newType == EdgeType.STAIR ||
			newType == EdgeType.LANDING_ATTACHED ||
			newType == EdgeType.LANDING) &&
		block.type === BlockType.DECKBOX &&
		block.dimensions.height <= MIN_STAIR_VIABLE_BLOCK_HEIGHT
	) {
		toastError({
			title: 'Deckbox height cannot accommodate landing',
			description: `You can’t add a ${
				newType.toLowerCase().includes('stair') ? 'stair' : 'landing'
			} to a Deckbox with height less than or equal to ${MIN_STAIR_VIABLE_BLOCK_HEIGHT} inches.`,
		});
		return state;
	}

	const parentEdge = block?.edges.find((edge) => edge.id == segment.edgeId);
	let newState = state;

	block.edges = block?.edges.map((edge) => {
		if (edge.id == segment.edgeId) {
			const updatedEdge = { ...edge };

			updatedEdge.segments = updatedEdge.segments.map((seg) => {
				if (seg.id == segment.id) {
					return { ...seg, type: newType };
				} else {
					return seg;
				}
			});

			return updatedEdge;
		} else {
			return edge;
		}
	});

	newState = updateBlock(newState, block);
	block = getBlock(newState.blocks, segment.blockId);
	const blockChildren = getBlockChildren(state.blocks, block);

	const childBlockAttachedToSegment = blockChildren.find(
		(child) => child.parentBlock.segmentId == segment.id
	);

	if (childBlockAttachedToSegment) {
		newState = deleteBlock(state, childBlockAttachedToSegment);
		block = getBlock(newState.blocks, segment.blockId);
	}

	if (
		(newType == EdgeType.STAIR_ATTACHED || newType == EdgeType.STAIR) &&
		block.dimensions.height > MIN_STAIR_VIABLE_BLOCK_HEIGHT
	) {
		const data = getDefaultStairForSegment({
			parentBlock: block,
			selectedBlockEdge: parentEdge,
			selectedBlockSegment: segment,
		});

		if (!data) {
			return newState;
		}

		const defaultStair = data.defaultStair;
		const addIntermediateLanding = data.addIntermediateLanding;
		const updatedOldSegment = data.updatedOldSegment;

		if (defaultStair && updatedOldSegment) {
			const updatedBlock = { ...block };

			updatedBlock.edges = updatedBlock?.edges.map((ed) => {
				if (ed.id == parentEdge?.id) {
					const updatedEdge = { ...parentEdge };
					const newSegments = [...updatedEdge.segments]; // Copy

					// Find index of the old segment to update it, and potentially insert new segment after
					const segIndex = newSegments.findIndex(
						(s) => s.id === updatedOldSegment.id
					);

					if (segIndex !== -1) {
						newSegments[segIndex] = { ...updatedOldSegment };

						// If there's a new segment (split), add it
						if (data.newSegment) {
							newSegments.splice(segIndex + 1, 0, data.newSegment);
						}
					}

					updatedEdge.segments = newSegments;
					return updatedEdge;
				} else {
					return ed;
				}
			});

			newState = updateBlock(newState, updatedBlock);

			newState = addStair(newState, defaultStair);

			if (!addIntermediateLanding) {
				return newState;
			} else {
				// Automatically create intermediate landing
				const stairBlock = getBlock(newState.blocks, defaultStair.id);
				const stairEndEdge = stairBlock?.edges.find(
					(e) => e.label === parentEdge?.label
				);
				const stairEndSegment = stairEndEdge?.segments[0];

				if (stairBlock && stairEndEdge && stairEndSegment) {
					const landingData = getDefaultLandingForSegment({
						parentBlock: stairBlock,
						selectedBlockEdge: stairEndEdge,
						selectedBlockSegment: stairEndSegment,
					});

					if (landingData?.defaultLanding) {
						const patchedLanding = {
							...landingData.defaultLanding,
							edges: landingData.defaultLanding.edges.map((edge) => {
								if (
									edge.label === parentEdge?.label &&
									!edge.attachedToParent
								) {
									return {
										...edge,
										segments: edge.segments.map((seg) => ({
											...seg,
											type: EdgeType.STAIR_ATTACHED,
										})),
									};
								}
								return edge;
							}),
						};
						newState = addLanding(newState, patchedLanding);

						// Automatically create second stair from landing to ground
						const landingBlock = getBlock(newState.blocks, patchedLanding.id);
						const landingEndEdge = landingBlock?.edges.find(
							(e) => e.label === parentEdge?.label
						);
						const landingEndSegment = landingEndEdge?.segments[0];

						if (landingBlock && landingEndEdge && landingEndSegment) {
							const secondStairData = getDefaultStairForSegment({
								parentBlock: landingBlock,
								selectedBlockEdge: landingEndEdge,
								selectedBlockSegment: landingEndSegment,
							});

							if (secondStairData?.defaultStair) {
								newState = addStair(newState, secondStairData.defaultStair);
							}
						}
					}
				}

				newState = selectEdge(newState, null, null);
			}
		}
	}

	if (
		(newType == EdgeType.LANDING_ATTACHED || newType == EdgeType.LANDING) &&
		block.dimensions.height > MIN_STAIR_VIABLE_BLOCK_HEIGHT
	) {
		// If the parent is a stair and we're attaching a landing to its END edge,
		// recalculate the stair's dimensions to split the height properly.
		if (block.type === BlockType.STAIR) {
			const stairAttachedEdge = block.edges?.find((e) => e.attachedToParent);
			const stairEndLabel = stairAttachedEdge
				? getOppositeEdge(stairAttachedEdge.label)
				: null;

			if (parentEdge?.label === stairEndLabel) {
				const startHeight =
					block.dimensions.startHeight || block.dimensions.height || 0;
				const newEndHeight = Math.ceil(startHeight / 2);
				const newHeight = startHeight - newEndHeight;
				const newNumberOfSteps = Math.ceil(newHeight / STEPS_COEFFICIENT);
				const newDepth = newNumberOfSteps * DEPTH_COEFFICIENT;

				block = {
					...block,
					dimensions: {
						...block.dimensions,
						endHeight: newEndHeight,
						height: newHeight,
						numberOfSteps: newNumberOfSteps,
						depth: newDepth,
					},
				};

				// Update display dimensions based on edge orientation
				const isTopBottom =
					stairAttachedEdge?.label === EdgeLabel.TOP ||
					stairAttachedEdge?.label === EdgeLabel.BOTTOM;
				block.displayDimensions = {
					width: isTopBottom ? block.dimensions.width : newDepth,
					height: isTopBottom ? newDepth : block.dimensions.width,
				};

				// Update side edges length to match new depth
				block.edges = block.edges.map((e) => {
					if (e.attachedToParent || e.label === stairEndLabel) return e;
					return {
						...e,
						length: newDepth,
						segments: e.segments.map((s) => ({
							...s,
							length: newDepth,
						})),
					};
				});

				// Persist the stair dimension changes to state
				newState = updateBlock(newState, block);
				block = getBlock(newState.blocks, segment.blockId);
			}
		}

		const data = getDefaultLandingForSegment({
			parentBlock: block,
			selectedBlockEdge: parentEdge,
			selectedBlockSegment: segment,
		});

		if (!data) {
			return newState;
		}

		const defaultLanding = data.defaultLanding;
		const updatedOldSegment = data.updatedOldSegment;

		if (defaultLanding && updatedOldSegment) {
			const updatedBlock = { ...block };

			updatedBlock.edges = updatedBlock?.edges.map((ed) => {
				if (ed.id == parentEdge?.id) {
					const updatedEdge = { ...ed };

					updatedEdge.segments = updatedEdge.segments.map((seg) => {
						if (seg.id == updatedOldSegment.id) {
							return { ...updatedOldSegment };
						}
						return seg;
					});

					return updatedEdge;
				} else {
					return ed;
				}
			});

			newState = updateBlock(newState, updatedBlock);

			newState = addLanding(newState, defaultLanding);

			newState = selectEdge(newState, null, null);
		}
	}

	if (
		(newType == EdgeType.DECKING || newType == EdgeType.DECKBOX) &&
		segment.length >= 12
	) {
		const data = getDefaultDeckboxForEdgeAndSegment({
			parentBlock: block,
			selectedBlockEdge: parentEdge,
			selectedBlockSegment: segment,
		});

		if (!data) {
			return newState;
		}

		const defaultDeckbox = data.defaultDeckbox;
		const updatedOldSegment = data.updatedOldSegment;

		if (defaultDeckbox && updatedOldSegment) {
			const updatedBlock = { ...block };

			updatedBlock.edges = updatedBlock?.edges.map((ed) => {
				if (ed.id == parentEdge?.id) {
					const updatedEdge = { ...ed };

					updatedEdge.segments = updatedEdge.segments.map((seg) => {
						if (seg.id == updatedOldSegment.id) {
							return { ...updatedOldSegment };
						}
						return seg;
					});

					return updatedEdge;
				} else {
					return ed;
				}
			});

			newState = updateBlock(newState, updatedBlock);

			newState = addBlock(newState, defaultDeckbox);

			newState = selectEdge(newState, null, null);
		}
	}

	if (newType == EdgeType.BUMPOUT) {
		const defaultBumpout = getDefaultBumpout(block, parentEdge);

		if (defaultBumpout) {
			const updatedBlock = { ...block };

			updatedBlock.edges = updatedBlock?.edges.map((ed) => {
				if (ed.id == parentEdge?.id) {
					const updatedEdge = { ...ed };

					updatedEdge.segments = updatedEdge.segments.map((seg) => {
						if (seg.id == segment.id) {
							return { ...seg, type: EdgeType.HOUSE };
						}
						return seg;
					});

					return updatedEdge;
				} else {
					return ed;
				}
			});

			newState = updateBlock(newState, updatedBlock);

			defaultBumpout.parentBlock.segmentId = segment.id;
			newState = addBumpout(newState, defaultBumpout);

			newState = selectEdge(newState, null, null);
		}
	}

	return newState;
}
export const deleteSegment = (state: DeckState, payload: any): DeckState => {
	const { block: targetBlock, edgeId, segmentId } = payload;

	const deleteFromBlock = (blk: Block): Block => {
		const attachedBlocks = [
			...(blk.bumpouts || []),
			...(blk.stairs || []),
			...(blk.landings || []),
		].filter((child) => child.parentBlock.segmentId == segmentId);

		const allChildren = attachedBlocks.reduce<Block[]>((acc, child) => {
			const children = getBlockChildren(state.blocks, child);
			return [...acc, ...children];
		}, []);

		const blocksToDeleteIds = [...attachedBlocks, ...allChildren].map(
			(b) => b.id
		);

		const updatedBlock: Block = {
			...blk,
			bumpouts: blk.bumpouts?.filter((b) => !blocksToDeleteIds.includes(b.id)),
			landings: blk.landings?.filter((l) => !blocksToDeleteIds.includes(l.id)),
			stairs: blk.stairs?.filter((s) => !blocksToDeleteIds.includes(s.id)),
		};

		updatedBlock.edges = updatedBlock.edges?.map((ed) => {
			if (ed.id !== edgeId) return ed;

			const updatedEdge = { ...ed };

			const segmentToDelete = updatedEdge.segments.find(
				(seg) => seg.id == segmentId
			);

			updatedEdge.segments = updatedEdge.segments.filter(
				(seg) => seg.id != segmentId
			);

			// Ensure at least one segment remains
			if (updatedEdge.segments.length === 0) {
				updatedEdge.segments.push({
					id: uuidv4(),
					edgeId: updatedEdge.id,
					blockId: blk.id,
					length: updatedEdge.length,
					type: EdgeType.RAILING,
				});
			}

			const segmentsLength = updatedEdge.segments.reduce(
				(acc, segment) => acc + segment.length,
				0
			);

			if (segmentsLength < updatedEdge.length) {
				const deckboxes = state.blocks.filter(
					(blk) => blk.type == BlockType.DECKBOX
				);

				const landings = deckboxes.flatMap((dk) => dk.landings || []);
				const stairs = deckboxes.flatMap((dk) => dk.stairs || []);
				const bumpouts = deckboxes.flatMap((dk) => dk.bumpouts || []);

				const allBlocks = [...deckboxes, ...landings, ...stairs, ...bumpouts];

				const vacantSegmentIndex = updatedEdge.segments.findIndex((segment) =>
					allBlocks.every((blk) => blk?.parentBlock?.segmentId != segment.id)
				);

				if (vacantSegmentIndex > -1) {
					const vacantSegment = updatedEdge.segments[vacantSegmentIndex];

					updatedEdge.segments.splice(vacantSegmentIndex, 1, {
						...vacantSegment,
						length:
							vacantSegment.length +
							Math.min(
								segmentToDelete?.length || 0,
								updatedEdge.length - segmentsLength
							),
					});
				}
			}

			return updatedEdge;
		});

		return updatedBlock;
	};

	const updatedBlocks = state.blocks.map((blk) => {
		if (blk.id === targetBlock.id) {
			return deleteFromBlock(blk);
		}

		if (blk.type === BlockType.DECKBOX) {
			let changed = false;
			const newBlk = { ...blk };

			if (newBlk.landings) {
				newBlk.landings = newBlk.landings.map((l) => {
					if (l.id === targetBlock.id) {
						changed = true;
						return deleteFromBlock(l);
					}
					return l;
				});
			}

			if (newBlk.stairs) {
				newBlk.stairs = newBlk.stairs.map((s) => {
					if (s.id === targetBlock.id) {
						changed = true;
						return deleteFromBlock(s);
					}
					return s;
				});
			}

			if (newBlk.bumpouts) {
				newBlk.bumpouts = newBlk.bumpouts.map((b) => {
					if (b.id === targetBlock.id) {
						changed = true;
						return deleteFromBlock(b);
					}
					return b;
				});
			}

			return changed ? newBlk : blk;
		}

		return blk;
	});

	return {
		...state,
		blocks: updatedBlocks,
		selectedBlock: null,
		selectedEdge: { blockId: null, edgeLabel: null },
		displayedBlockModal: null,
	};
};
